import { useCallback, useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from 'pricing-pages-ui-components/hooks/useMedia';
export const ANIMATION_DELAY_MS = 5000;
export const TRACE_DURATION_MS = 7200;
const PRICING_CARDS_ELEMENT_ID = 'hsoc-tier-cards';
const HMC_SESSION_KEY = 'pricing-pages.hmc-state';

// 'animated' — animation played but button not yet clicked (re-animate on reload)
// 'clicked'  — button was clicked; never animate again this session
function readSessionState() {
  try {
    const val = sessionStorage.getItem(HMC_SESSION_KEY);
    if (val === 'animated' || val === 'clicked') return val;
    return null;
  } catch (_unused) {
    return null;
  }
}
function writeSessionState(state) {
  try {
    sessionStorage.setItem(HMC_SESSION_KEY, state);
  } catch (_unused2) {
    // sessionStorage may be blocked in private mode.
  }
}
function clearTimer(ref) {
  if (ref.current != null) {
    window.clearTimeout(ref.current);
    ref.current = undefined;
  }
}
export function useHelpMeChooseAffordance() {
  const prefersReducedMotion = usePrefersReducedMotion();

  // Read once at mount; stable for the lifetime of this hook instance.
  const [sessionState] = useState(readSessionState);

  // Case 1 ('animated') and Case 2 ('clicked') both start as settled so the
  // tertiary button is visible immediately on reload. Case 3 (null) starts idle.
  const [phase, setPhase] = useState(sessionState !== null ? 'settled' : 'idle');

  // True only when the button was clicked — the one condition that suppresses re-animation.
  const hasOpenedRef = useRef(sessionState === 'clicked');
  const hasAnimatedRef = useRef(false);
  const hasArmedRef = useRef(false);
  const delayTimerRef = useRef(undefined);
  const traceTimerRef = useRef(undefined);
  const startTrace = useCallback(() => {
    if (hasAnimatedRef.current || hasOpenedRef.current) return;
    if (prefersReducedMotion) {
      setPhase('settled');
      return;
    }
    hasAnimatedRef.current = true;
    setPhase('tracing');
    traceTimerRef.current = window.setTimeout(() => {
      setPhase('settled');
      writeSessionState('animated');
    }, TRACE_DURATION_MS);
  }, [prefersReducedMotion]);

  // Starts (or restarts) the 5s quiet-period timer before the animation fires.
  const armDelayTimer = useCallback(() => {
    clearTimer(delayTimerRef);
    delayTimerRef.current = window.setTimeout(() => {
      if (hasAnimatedRef.current || hasOpenedRef.current) return;
      startTrace();
    }, ANIMATION_DELAY_MS);
  }, [startTrace]);

  // Any interaction on the cards (move, scroll, key, click) resets the full 5s clock.
  useEffect(() => {
    const cardsEl = document.getElementById(PRICING_CARDS_ELEMENT_ID);
    if (!cardsEl) return undefined;
    const restartDelay = () => {
      if (hasAnimatedRef.current || hasOpenedRef.current) return;
      armDelayTimer();
    };
    cardsEl.addEventListener('pointermove', restartDelay, {
      passive: true
    });
    cardsEl.addEventListener('wheel', restartDelay, {
      passive: true
    });
    cardsEl.addEventListener('keydown', restartDelay);
    cardsEl.addEventListener('click', restartDelay);
    return () => {
      cardsEl.removeEventListener('pointermove', restartDelay);
      cardsEl.removeEventListener('wheel', restartDelay);
      cardsEl.removeEventListener('keydown', restartDelay);
      cardsEl.removeEventListener('click', restartDelay);
    };
  }, [armDelayTimer]);

  // Escape during the animation skips straight to the settled state.
  useEffect(() => {
    if (phase !== 'tracing') return undefined;
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        if (traceTimerRef.current != null) window.clearTimeout(traceTimerRef.current);
        hasAnimatedRef.current = true;
        setPhase('settled');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase]);

  // Cleanup timers on unmount.
  useEffect(() => () => {
    clearTimer(delayTimerRef);
    clearTimer(traceTimerRef);
  }, []);
  const notifyVisible = useCallback(() => {
    if (hasArmedRef.current) return;
    hasArmedRef.current = true;
    if (hasOpenedRef.current || prefersReducedMotion) {
      setPhase('settled');
      return;
    }
    armDelayTimer();
  }, [prefersReducedMotion, armDelayTimer]);
  const notifyOpened = useCallback(() => {
    hasOpenedRef.current = true;
    setPhase('settled');
    writeSessionState('clicked');
    clearTimer(delayTimerRef);
    clearTimer(traceTimerRef);
  }, []);
  return {
    phase,
    notifyVisible,
    notifyOpened
  };
}